import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, DialogContent, DialogTitle, Typography, useMediaQuery, Box } from '@mui/material';
import { CustomTextField } from '../TextInput/CustomTextField';
import { IIntroductionType } from './introductionSteps.data';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ThemeStyle } from '../../module/ThemeManager';
import { theme } from '../../constants/theme';
import { LottieAnimation } from '../Animation/Animation';
import { arrowAnimationBlack } from '../../assets/animations';
import { useThemeContext } from '../../hook/useThemeManager';

export interface ITabSelector {
    onChange?: (selected: string[]) => void;
    step: IIntroductionType;
    themeStyle?: ThemeStyle;
    maxSelection?: number;
    minSelection?: number;
    onSelect?: (option: string) => void;
    skipRefresh?: boolean;
    shouldShowButton?: boolean;
    onClickButton?: () => void;
    disableButton?: boolean;
    maxSelectedDisableState?: boolean;
    initialValue?: string[];
    buttonText?: string;
    isStep?: boolean;
    tabsStyle?: React.CSSProperties;
    pillStyle?: React.CSSProperties;
    pillTextStyle?: React.CSSProperties;
    disableAnimation?: boolean;
    onAddCustomOption?: (customValue: string) => void;
    enableCustomOptions?: boolean;
}

const TabSelector: React.FC<ITabSelector> = ({
    step,
    isStep,
    onChange,
    // themeStyle,
    maxSelection,
    minSelection,
    onSelect,
    skipRefresh,
    shouldShowButton,
    onClickButton,
    disableButton,
    maxSelectedDisableState,
    initialValue,
    buttonText,
    tabsStyle,
    pillStyle,
    pillTextStyle,
    disableAnimation,
    onAddCustomOption,
    enableCustomOptions,
}) => {
    const [selected, setSelected] = useState<string[]>(initialValue || []);
    const [currentPage, setCurrentPage] = useState(0);
    const [currentOptions, setCurrentOptions] = useState<string[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');
    const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
    const [availableOptions, setAvailableOptions] = useState<string[]>(step.options || []);
    const optionsPerPage = 10;
    const start = currentPage * optionsPerPage;
    const end = start + optionsPerPage;
    const isMobile = useMediaQuery('(max-width: 600px)');
    const { themeStyle } = useThemeContext();

    // Update available options when step.options changes
    useEffect(() => {
        setAvailableOptions(step.options || []);
    }, [step.options]);

    const handleSelection = (option: string) => {
        const isSelected = isAlreadySelected(option);

        if (maxSelection === 1) {
            if (!isSelected) {
                setSelected([option]);
                onChange && onChange([option]);
            } else {
                setSelected([]);
                onChange && onChange([]);
            }
        } else {
            if (!isSelected && (!maxSelection || selected.length < maxSelection)) {
                const newSelected = [...selected, option];
                setSelected(newSelected);
                onChange && onChange(newSelected);
            } else {
                const filtered = selected.filter(filter => filter !== option);
                setSelected(filtered);
                onChange && onChange(filtered);
            }
        }
    };

    const nextButtonStyle = isMobile
        ? { bottom: 20, left: '50%', transform: 'translateX(-50%)' }
        : { right: 20, top: '50%', transform: 'translateY(-50%)' };

    const prevButtonStyle = isMobile
        ? { top: 20, left: '50%', transform: 'translateX(-50%)' }
        : { left: 20, top: '50%', transform: 'translateY(-50%)' };

    useEffect(() => {
        if (!skipRefresh) {
            setSelected([]);
        }
    }, [step, skipRefresh, initialValue, themeStyle]);

    useEffect(() => {
        // Filter options based on search
        if (searchValue.trim()) {
            const filtered = availableOptions.filter(option =>
                option.toLowerCase().includes(searchValue.toLowerCase()),
            );
            setFilteredOptions(filtered);
            setCurrentOptions(filtered.slice(start, end));
        } else {
            setFilteredOptions([]);
            setCurrentOptions(availableOptions.slice(start, end));
        }
    }, [currentPage, end, start, availableOptions, searchValue]);

    // Reset pagination when search changes
    useEffect(() => {
        if (searchValue.trim()) {
            setCurrentPage(0);
        }
    }, [searchValue]);

    const isAlreadySelected = (option: string) => selected.some(selection => selection === option);

    const handleSearchEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchValue.trim()) {
            e.preventDefault();
            const trimmedValue = searchValue.trim();

            // Check if already selected
            if (isAlreadySelected(trimmedValue)) {
                setSearchValue('');
                return;
            }

            // Check maxSelection limit
            if (maxSelection && selected.length >= maxSelection) {
                return;
            }

            // Add to options if it doesn't exist
            if (!availableOptions.includes(trimmedValue)) {
                const newOptions = [...availableOptions, trimmedValue];
                setAvailableOptions(newOptions);
                // Notify parent component
                if (onAddCustomOption) {
                    onAddCustomOption(trimmedValue);
                }
            }

            // Add to selected
            if (maxSelection === 1) {
                setSelected([trimmedValue]);
                onChange && onChange([trimmedValue]);
            } else {
                const newSelected = [...selected, trimmedValue];
                setSelected(newSelected);
                onChange && onChange(newSelected);
            }

            // Clear search
            setSearchValue('');
        }
    };

    return (
        <AnimatePresence mode="wait">
            <motion.div
                initial={disableAnimation ? false : { opacity: 0 }}
                animate={disableAnimation ? false : { opacity: 1 }}
                exit={
                    disableAnimation
                        ? undefined
                        : {
                              opacity: 0,
                              transition: { ease: 'easeIn', duration: 1.0 },
                          }
                }
                key={step.displayTitle}
                style={{
                    maxWidth: isMobile ? '90vw' : '550px',
                    width: isMobile ? '90vw' : '550px',
                    minWidth: isMobile ? '90vw' : '550px',
                }}
                transition={disableAnimation ? undefined : { duration: 3.5 }}>
                <DialogTitle
                    style={{
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        color: theme[themeStyle].primary_white_orange,
                        ...(isStep && { color: theme[themeStyle].tag_white }),
                    }}
                    sx={{
                        fontSize: {
                            xs: 24,
                            sm: 32,
                        },
                    }}>
                    {step.displayTitle}
                </DialogTitle>
                {/* Search Bar */}
                {enableCustomOptions && (
                    <Box
                        sx={{
                            padding: '0 24px',
                            marginBottom: '16px',
                            width: '100%',
                            minWidth: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            '& > div': {
                                width: '100% !important',
                                minWidth: '100% !important',
                                maxWidth: '100% !important',
                            },
                        }}>
                        <CustomTextField
                            label=""
                            textFieldProps={{
                                InputProps: {
                                    disableUnderline: true,
                                    placeholder: 'Type to search or press Enter to add custom value...',
                                    value: searchValue,
                                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                        setSearchValue(e.target.value);
                                    },
                                    onKeyDown: handleSearchEnter,
                                },
                                style: {
                                    width: '100%',
                                    minWidth: '100%',
                                    maxWidth: '100%',
                                    backgroundColor: theme[themeStyle].form_gray,
                                    borderRadius: 8,
                                    padding: '0 14px',
                                    boxSizing: 'border-box',
                                },
                                inputProps: {
                                    style: {
                                        color: theme[themeStyle].lightWhite,
                                        padding: '15px 0',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        width: '100%',
                                        minWidth: '100%',
                                        boxSizing: 'border-box',
                                    },
                                },
                                sx: {
                                    width: '100%',
                                    minWidth: '100%',
                                    maxWidth: '100%',
                                    '& .MuiInputBase-root': {
                                        width: '100% !important',
                                        minWidth: '100% !important',
                                        maxWidth: '100% !important',
                                        height: '56px !important',
                                        minHeight: '56px !important',
                                    },
                                    '& .MuiInputBase-input': {
                                        width: '100% !important',
                                        minWidth: '100% !important',
                                        height: '56px !important',
                                        minHeight: '56px !important',
                                        padding: '15px 0 !important',
                                        boxSizing: 'border-box !important',
                                    },
                                },
                            }}
                        />
                        {maxSelection && (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '10px',
                                    color: theme[themeStyle].gray_message,
                                    fontStyle: 'italic',
                                    marginTop: '4px',
                                    paddingLeft: '14px',
                                    textAlign: 'right',
                                }}>
                                Select {maxSelection} max.
                            </Typography>
                        )}
                    </Box>
                )}
                <DialogContent
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        width: '100%',
                        minWidth: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        ...tabsStyle,
                    }}>
                    {currentOptions &&
                        currentOptions.map((option, index) => (
                            <motion.div
                                key={index}
                                initial={disableAnimation ? false : { opacity: 0, scale: 0 }}
                                animate={disableAnimation ? false : { opacity: 1, scale: 1 }}
                                onClick={() => handleSelection(option)}
                                transition={disableAnimation ? undefined : { delay: index * 0.2, duration: 0.5 }}
                                style={{ margin: '5px', cursor: 'pointer', ...pillStyle }}>
                                <Typography
                                    sx={{
                                        paddingX: 2,
                                        paddingY: 1,
                                        display: '-webkit-box',
                                        overflow: 'hidden',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 1, //numOfLines
                                    }}
                                    style={{
                                        ...pillTextStyle,
                                        borderRadius: '50px',
                                        opacity: maxSelectedDisableState && !isAlreadySelected(option) ? 0.5 : 1,
                                        textTransform: 'capitalize',
                                        fontFamily: isAlreadySelected(option)
                                            ? FONT_ENUM.MAIN_BOLD
                                            : FONT_ENUM.MAIN_MEDIUM,
                                        border: `1.0px solid ${theme[themeStyle].tag_white}`,
                                        borderColor: isAlreadySelected(option)
                                            ? theme[themeStyle].primary_orange
                                            : theme[themeStyle].tag_white,
                                        color: isAlreadySelected(option)
                                            ? theme[themeStyle].black
                                            : theme[themeStyle].tag_white,
                                        backgroundColor: isAlreadySelected(option)
                                            ? theme[themeStyle].highlight
                                            : 'transparent',

                                        ...(isStep && {
                                            border: `1.0px solid ${theme[themeStyle].lightWhite}`,
                                            borderColor: isAlreadySelected(option)
                                                ? theme[themeStyle].primary_orange
                                                : theme[themeStyle].lightWhite,
                                            color: isAlreadySelected(option)
                                                ? theme[themeStyle].primary_white
                                                : theme[themeStyle].tag_white,
                                            backgroundColor: isAlreadySelected(option)
                                                ? theme[themeStyle].highlight
                                                : 'transparent',
                                        }),
                                    }}>
                                    {option}
                                </Typography>
                            </motion.div>
                        ))}
                </DialogContent>
                {step.instruction && (
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].true_white,
                            paddingLeft: '30px',
                        }}
                        sx={{
                            fontSize: {
                                xs: 12,
                                sm: 14,
                            },
                        }}>
                        {step.instruction}
                    </Typography>
                )}
                <div style={{ marginTop: 20, marginLeft: 30 }}>
                    {currentPage > 0 && (
                        <Button
                            size="small"
                            sx={{ color: theme[themeStyle].highlight, borderColor: theme[themeStyle].highlight }}
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                            disabled={currentPage === 0}
                            style={{ position: 'fixed', ...prevButtonStyle }}>
                            <LottieAnimation
                                animationDirection={isMobile ? 'up' : 'left'}
                                animationData={arrowAnimationBlack}
                            />
                        </Button>
                    )}
                    {step && step.options && !step.hideArrow && (
                        <Button
                            size="small"
                            sx={{ color: theme[themeStyle].highlight, borderColor: theme[themeStyle].highlight }}
                            onClick={() => {
                                const optionsToUse = searchValue.trim() ? filteredOptions : availableOptions;
                                const currentEnd = (currentPage + 1) * optionsPerPage;
                                if (currentEnd < optionsToUse.length) {
                                    setCurrentPage(prev => prev + 1);
                                }
                            }}
                            style={{ position: 'fixed', ...nextButtonStyle }}
                            disabled={
                                searchValue.trim()
                                    ? (currentPage + 1) * optionsPerPage >= filteredOptions.length
                                    : end >= availableOptions.length
                            }>
                            <LottieAnimation
                                animationDirection={isMobile ? 'down' : 'right'}
                                animationData={arrowAnimationBlack}
                            />
                        </Button>
                    )}
                </div>
                {shouldShowButton && (
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={onClickButton}
                        disabled={disableButton}
                        sx={{ color: theme[themeStyle].highlight, borderColor: theme[themeStyle].highlight }}
                        style={{
                            borderRadius: 50,
                            fontFamily: FONT_ENUM.MEDIUM_HEADER,
                            paddingRight: 60,
                            paddingLeft: 60,
                            marginLeft: 30,
                            color: theme[themeStyle].true_white,
                            borderColor: theme[themeStyle].primary_white_orange,
                        }}>
                        {buttonText}
                    </Button>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export default TabSelector;
